import { getRecordTypeDisplay } from "../../../types";

const RECORD_TYPE_CANONICAL_MAP: Record<string, string> = {
  MEAL_MONITORING: "MONITORING",
  MEAL_MONITORING_WITH_VITALS: "MONITORING",
  MWST_G: "MWST",
  MWST_G_DATA: "MWST",
  FOOD_TEST_G: "FOOD_TEST",
  FOOD_TEST_G_DATA: "FOOD_TEST",
  GOST_DATA: "GOST",
  GOST_DATA_2: "GOST",
  VS_DATA: "VS",
  TOMASS_DATA: "TOMASS",
  SSTP_DATA: "SSTP",
};

const JA_PRIMARY_OVERRIDES: Record<string, string> = {
  RSST: "反復唾液嚥下テスト",
  VS: "ビデオ聴診記録",
};

const ZH_TW_PRIMARY_OVERRIDES: Record<string, string> = {
  RSST: "重複唾液吞嚥測試",
  VS: "影片聽診記錄",
};

const RECORD_TYPE_ICON_MAP: Record<string, string> = {
  MONITORING: "monitor_heart",
  MWST: "water_drop",
  FOOD_TEST: "restaurant",
  GOST: "fitness_center",
  VS: "videocam",
  TOMASS: "timer",
  SSTP: "local_drink",
  RSST: "repeat",
};

type ProtocolLocale = "ja" | "en" | "zh-TW";

export function normalizeRecordType(recordType?: string | null): string {
  if (!recordType) {
    return "";
  }
  const normalized = recordType.trim().toUpperCase();
  return RECORD_TYPE_CANONICAL_MAP[normalized] ?? normalized;
}

export function getRecordTypePrimaryLabel(
  recordType?: string | null,
  fallbackLabel?: string | null,
  locale: ProtocolLocale = "ja",
): string {
  const normalized = normalizeRecordType(recordType);
  if (!normalized) {
    return fallbackLabel ?? "";
  }

  if (locale === "ja") {
    const jaOverride = JA_PRIMARY_OVERRIDES[normalized];
    if (jaOverride) {
      return jaOverride;
    }
  }

  if (locale === "zh-TW") {
    const zhTwOverride = ZH_TW_PRIMARY_OVERRIDES[normalized];
    if (zhTwOverride) {
      return zhTwOverride;
    }
  }

  return (
    getRecordTypeDisplay(normalized, locale) ?? fallbackLabel ?? normalized
  );
}

export function getRecordTypeSecondaryLabel(
  recordType?: string | null,
  primaryLabel?: string | null,
): string {
  const normalized = normalizeRecordType(recordType);
  if (!normalized) {
    return "";
  }

  const secondary = getRecordTypeDisplay(normalized, "en");
  if (!secondary) {
    return "";
  }

  const primary = (primaryLabel ?? "").toLowerCase();
  if (primary.includes(secondary.toLowerCase())) {
    return "";
  }

  return secondary;
}

export function buildRecordTypeSearchText(
  recordType?: string | null,
  recordTypeView?: string | null,
): string {
  const normalized = normalizeRecordType(recordType);
  const jaLabel = getRecordTypePrimaryLabel(normalized, recordTypeView, "ja");
  const enLabel =
    getRecordTypeDisplay(normalized || (recordType ?? ""), "en") ?? "";
  const rawCode = (recordType ?? "").replace(/_/g, " ");
  const canonicalCode = normalized.replace(/_/g, " ");

  return [
    recordTypeView ?? "",
    jaLabel,
    enLabel,
    rawCode,
    canonicalCode,
    normalized,
  ]
    .filter((value) => !!value.trim())
    .join(" ")
    .toLowerCase();
}

export function getRecordTypeIcon(recordType?: string | null): string {
  const normalized = normalizeRecordType(recordType);
  if (!normalized) {
    return "category";
  }
  return RECORD_TYPE_ICON_MAP[normalized] ?? "category";
}
